import { useState } from 'react';
import { useQuery, useMutation } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import {
  TrendingUp, TrendingDown, Minus, Activity, AlertTriangle, Bot, Clock,
  ExternalLink, BarChart3, X,
} from 'lucide-react';
import { analytics, type SeitenZahlen, type ErreichbarkeitsBefund } from '../api';
import StatistikPanel from '../components/StatistikPanel';

/**
 * Reichweite und Erreichbarkeit aller Seiten.
 *
 * ══ WARUM NEBEN DER EINZELANSICHT ══════════════════════════════════════════
 *
 * Die Statistik je Instanz gibt es auf deren Detailseite. Was fehlte, ist die
 * Frage davor: welche Seiten laufen gut, welche gar nicht, und wo hat sich
 * etwas verändert. Wer das bisher wissen wollte, klickte zwanzig Instanzen
 * durch — und tat es deshalb nicht.
 *
 * ══ DIESELBE AMPEL WIE AUF DER INSTANZSEITE ════════════════════════════════
 *
 * Grün antwortet, gelb antwortet mit Einschränkung (Zugang geschützt,
 * Weiterleitung woandershin), rot antwortet nicht. Bewusst derselbe Prüfer
 * wie in der Adressübersicht: zwei Urteile über dieselbe Frage laufen
 * auseinander, und wer beides nebeneinander sieht, hält eines für kaputt.
 *
 * ══ OHNE MESSUNG HEISST NICHT OHNE BESUCHER ════════════════════════════════
 *
 * Instanzen ohne eingeschaltete Reichweitenmessung sind standardmäßig
 * ausgeblendet. Sie stünden sonst mit lauter Nullen in der Liste und sähen aus
 * wie tote Seiten — dabei ist nur der Zähler aus.
 */

const ZEITRAEUME: Array<{ wert: string; text: string }> = [
  { wert: 'heute', text: 'heute' },
  { wert: 'gestern', text: 'gestern' },
  { wert: '7d', text: '7 Tage' },
  { wert: '28d', text: '28 Tage' },
  { wert: '90d', text: '90 Tage' },
  { wert: '12m', text: '12 Monate' },
];

function zahl(n: number): string {
  return n.toLocaleString('de-DE');
}

/**
 * Die Ampel zu einer Adresse — wie in der Adressübersicht.
 *
 * Grau heisst „noch nicht geprüft" und nicht „in Ordnung": ein Punkt, der
 * schweigt, während er lädt, würde als Entwarnung gelesen.
 */
function Ampel({ befund, laedt }: { befund?: ErreichbarkeitsBefund; laedt?: boolean }) {
  if (laedt && !befund) {
    return (
      <span
        className="inline-block h-2 w-2 shrink-0 animate-pulse rounded-full bg-slate-300"
        title="wird geprüft …"
      />
    );
  }
  if (!befund) {
    return (
      <span
        className="inline-block h-2 w-2 shrink-0 rounded-full bg-slate-200"
        title="nicht geprüft"
      />
    );
  }
  const farbe =
    befund.ampel === 'gruen'
      ? 'bg-emerald-500'
      : befund.ampel === 'gelb'
        ? 'bg-amber-500'
        : 'bg-red-500';
  return (
    <span
      className={`inline-block h-2 w-2 shrink-0 rounded-full ${farbe}`}
      title={`${befund.befund} (${befund.dauerMs} ms)`}
    />
  );
}

/** Kleiner Verlauf ohne Bibliothek — Balken reichen und lesen sich schnell. */
function Verlauf({ werte }: { werte: number[] }) {
  if (!werte.length) return <span className="text-[10px] text-slate-300">—</span>;
  const max = Math.max(...werte, 1);
  const schritt = Math.ceil(werte.length / 40);
  const gezeigt = werte.filter((_, i) => i % schritt === 0);
  return (
    <span className="inline-flex h-6 items-end gap-px" title={`${werte.length} Tage`}>
      {gezeigt.map((w, i) => (
        <span
          key={i}
          className="w-1 rounded-sm bg-brand-400"
          style={{ height: `${Math.max(2, (w / max) * 22)}px` }}
        />
      ))}
    </span>
  );
}

function Wandel({ prozent }: { prozent: number | null }) {
  if (prozent === null) return <span className="text-[11px] text-slate-300">—</span>;
  if (prozent === 0) {
    return (
      <span className="inline-flex items-center gap-0.5 text-[11px] text-slate-500">
        <Minus size={10} /> 0 %
      </span>
    );
  }
  const hoch = prozent > 0;
  return (
    <span
      className={`inline-flex items-center gap-0.5 text-[11px] ${
        hoch ? 'text-emerald-700' : 'text-rose-700'
      }`}
    >
      {hoch ? <TrendingUp size={10} /> : <TrendingDown size={10} />}
      {hoch ? '+' : ''}
      {prozent} %
    </span>
  );
}

/**
 * Die vollen Zahlen einer Instanz, ohne die Seite zu verlassen.
 *
 * Dasselbe Panel wie auf der Instanzseite — nicht nachgebaut, sondern
 * eingebunden. Eine zweite Darstellung derselben Zahlen wäre eine zweite
 * Stelle, an der etwas veralten kann.
 */
function StatistikModal({
  instanceId,
  name,
  onClose,
}: {
  instanceId: string;
  name: string;
  onClose: () => void;
}) {
  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="mt-8 flex max-h-[88vh] w-full max-w-5xl flex-col overflow-hidden rounded-xl bg-surface shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex shrink-0 items-center justify-between border-b border-slate-200 px-5 py-3">
          <div>
            <h2 className="flex items-center gap-1.5 text-base font-semibold text-slate-900">
              <BarChart3 size={16} /> {name}
            </h2>
            <Link
              to={`/instances/${instanceId}`}
              className="text-xs text-brand-700 hover:underline"
            >
              zur Instanz →
            </Link>
          </div>
          <button
            onClick={onClose}
            className="rounded p-1 text-slate-400 hover:bg-slate-100"
            aria-label="Schliessen"
          >
            <X size={16} />
          </button>
        </div>
        <div className="overflow-y-auto px-5 py-4">
          <StatistikPanel instanceId={instanceId} stackName={name} />
        </div>
      </div>
    </div>
  );
}

export default function AnalyticsPage() {
  const [zeitraum, setZeitraum] = useState('28d');
  // Vorgabe: ohne Messung ausblenden. Sie stünden sonst mit lauter Nullen da
  // und sähen aus wie tote Seiten.
  const [ohneMessungZeigen, setOhneMessungZeigen] = useState(false);
  const [befunde, setBefunde] = useState<Map<string, ErreichbarkeitsBefund>>(new Map());
  const [offen, setOffen] = useState<{ id: string; name: string } | null>(null);

  const daten = useQuery({
    queryKey: ['analytics', 'seiten', zeitraum],
    queryFn: () => analytics.seiten(zeitraum),
  });

  const pruefen = useMutation({
    mutationFn: () => analytics.erreichbarkeit(),
    onSuccess: (liste) => setBefunde(new Map(liste.map((e) => [e.instanceId, e]))),
  });

  const alle = daten.data?.seiten ?? [];
  const seiten = ohneMessungZeigen ? alle : alle.filter((s) => s.misst || s.besuche > 0);
  const sortiert = [...seiten].sort((a, b) => b.besuche - a.besuche);

  const summe = daten.data?.summe;
  const gesamtWandel =
    summe && summe.besucheVorher > 0
      ? Math.round(((summe.besuche - summe.besucheVorher) / summe.besucheVorher) * 100)
      : null;

  const rot = [...befunde.values()].filter((e) => e.ampel === 'rot');
  const gelb = [...befunde.values()].filter((e) => e.ampel === 'gelb');
  const ohneMessung = alle.filter((s) => !s.misst).length;

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold text-slate-800">Analytics</h1>
          <p className="mt-0.5 text-xs text-slate-500">
            Was auf unseren Seiten ankommt — und ob sie überhaupt erreichbar sind.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={zeitraum}
            onChange={(e) => setZeitraum(e.target.value)}
            className="rounded border border-slate-300 px-2 py-1.5 text-xs"
          >
            {ZEITRAEUME.map((z) => (
              <option key={z.wert} value={z.wert}>
                {z.text}
              </option>
            ))}
          </select>
          <button
            onClick={() => pruefen.mutate()}
            disabled={pruefen.isPending}
            title="Ruft jede Seite tatsächlich ab. Dauert einige Sekunden."
            className="inline-flex items-center gap-1.5 rounded-lg border border-slate-300 px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-40"
          >
            <Activity size={13} className={pruefen.isPending ? 'animate-pulse' : ''} />
            {pruefen.isPending ? 'ruft ab …' : 'Erreichbarkeit prüfen'}
          </button>
        </div>
      </div>

      {/* Kopfzahlen */}
      <div className="grid gap-3 sm:grid-cols-4">
        <div className="rounded-xl border border-slate-200 bg-surface p-4">
          <div className="text-[11px] font-medium text-slate-500">Besuche</div>
          <div className="mt-1 text-xl font-semibold tabular-nums text-slate-800">
            {summe ? zahl(summe.besuche) : '—'}
          </div>
          <div className="mt-1">
            <Wandel prozent={gesamtWandel} />
            <span className="ml-1 text-[11px] text-slate-400">gegenüber davor</span>
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-surface p-4">
          <div className="text-[11px] font-medium text-slate-500">Aufrufe</div>
          <div className="mt-1 text-xl font-semibold tabular-nums text-slate-800">
            {summe ? zahl(summe.aufrufe) : '—'}
          </div>
          <div className="mt-1 text-[11px] text-slate-400">
            {summe && summe.besuche > 0
              ? `${(summe.aufrufe / summe.besuche).toFixed(1)} je Besuch`
              : '—'}
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-surface p-4">
          <div className="text-[11px] font-medium text-slate-500">Seiten mit Messung</div>
          <div className="mt-1 text-xl font-semibold tabular-nums text-slate-800">
            {alle.length - ohneMessung}
            <span className="text-sm font-normal text-slate-400"> / {alle.length}</span>
          </div>
          {ohneMessung > 0 && (
            <button
              onClick={() => setOhneMessungZeigen((v) => !v)}
              className="mt-1 text-[11px] text-brand-700 underline"
            >
              {ohneMessungZeigen ? 'ohne Messung ausblenden' : `${ohneMessung} ohne Messung zeigen`}
            </button>
          )}
        </div>
        <div
          className={`rounded-xl border p-4 ${
            rot.length > 0
              ? 'border-rose-200 bg-rose-50'
              : gelb.length > 0
                ? 'border-amber-200 bg-amber-50'
                : 'border-slate-200 bg-surface'
          }`}
        >
          <div className="text-[11px] font-medium text-slate-500">Erreichbarkeit</div>
          {befunde.size === 0 ? (
            <div className="mt-1 text-xs text-slate-400">noch nicht geprüft</div>
          ) : (
            <>
              <div className="mt-1 flex items-baseline gap-2">
                <span className="text-xl font-semibold tabular-nums text-emerald-700">
                  {befunde.size - rot.length - gelb.length}
                </span>
                {gelb.length > 0 && (
                  <span className="text-sm font-semibold tabular-nums text-amber-700">
                    {gelb.length}
                  </span>
                )}
                {rot.length > 0 && (
                  <span className="text-sm font-semibold tabular-nums text-rose-700">
                    {rot.length}
                  </span>
                )}
                <span className="text-sm font-normal text-slate-400">/ {befunde.size}</span>
              </div>
              <div className="mt-1 text-[11px] text-slate-500">grün · gelb · rot</div>
            </>
          )}
        </div>
      </div>

      {/* Was nicht grün ist, zuerst */}
      {(rot.length > 0 || gelb.length > 0) && (
        <div
          className={`rounded-xl border p-3 ${
            rot.length > 0 ? 'border-rose-200 bg-rose-50' : 'border-amber-200 bg-amber-50'
          }`}
        >
          <h2
            className={`flex items-center gap-1.5 text-xs font-semibold ${
              rot.length > 0 ? 'text-rose-800' : 'text-amber-800'
            }`}
          >
            <AlertTriangle size={13} /> Nicht in Ordnung
          </h2>
          <ul className="mt-1.5 space-y-1">
            {[...rot, ...gelb].map((e) => {
              const s = alle.find((x) => x.instanceId === e.instanceId);
              return (
                <li key={e.instanceId} className="flex flex-wrap items-center gap-2 text-[11px]">
                  <Ampel befund={e} />
                  <Link
                    to={`/instances/${e.instanceId}`}
                    className="font-medium text-slate-800 hover:underline"
                  >
                    {s?.name ?? e.instanceId.slice(0, 8)}
                  </Link>
                  <span className="font-mono text-slate-600">{e.hostname}</span>
                  <span className="text-slate-600">{e.befund}</span>
                </li>
              );
            })}
          </ul>
        </div>
      )}

      {/* Tabelle */}
      <div className="overflow-hidden rounded-xl border border-slate-200 bg-surface">
        <div className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-200 px-4 py-2">
          <span className="text-xs text-slate-500">
            {daten.data ? `${daten.data.von} bis ${daten.data.bis}` : 'wird geladen …'}
          </span>
          <label className="flex items-center gap-1.5 text-[11px] text-slate-600">
            <input
              type="checkbox"
              checked={ohneMessungZeigen}
              onChange={(e) => setOhneMessungZeigen(e.target.checked)}
            />
            auch Seiten ohne Messung
          </label>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="border-b border-slate-200 bg-slate-50 text-[11px] text-slate-600">
              <tr>
                <th className="px-3 py-2 text-right font-medium">Nr.</th>
                <th className="px-3 py-2 font-medium">Seite</th>
                <th className="px-3 py-2 font-medium">Kunde</th>
                <th className="px-3 py-2 text-right font-medium">Besuche</th>
                <th className="px-3 py-2 text-right font-medium">Aufrufe</th>
                <th className="px-3 py-2 font-medium">Wandel</th>
                <th className="px-3 py-2 font-medium">Verlauf</th>
                <th
                  className="px-3 py-2 text-right font-medium"
                  title="Anteil Besuche mit nur einer Seite"
                >
                  einzeln
                </th>
                <th className="px-3 py-2 text-right font-medium">Verweildauer</th>
                <th className="px-3 py-2 font-medium">abrufbar</th>
                <th className="px-3 py-2 font-medium" />
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {sortiert.map((s: SeitenZahlen) => {
                const e = befunde.get(s.instanceId);
                return (
                  <tr key={s.instanceId} className={!s.misst ? 'bg-slate-50/50' : undefined}>
                    <td className="px-3 py-1.5 text-right font-mono tabular-nums text-slate-400">
                      {s.nummer ?? '—'}
                    </td>
                    <td className="px-3 py-1.5">
                      <Link
                        to={`/instances/${s.instanceId}`}
                        className="font-medium text-slate-800 hover:underline"
                      >
                        {s.name}
                      </Link>
                      {s.domain && (
                        <a
                          href={`https://${s.domain}`}
                          target="_blank"
                          rel="noreferrer"
                          className="ml-1.5 inline-flex items-center gap-0.5 font-mono text-[10px] text-slate-400 hover:text-brand-600"
                        >
                          {s.domain} <ExternalLink size={8} />
                        </a>
                      )}
                      {!s.misst && (
                        <span
                          className="ml-1.5 rounded bg-slate-200 px-1 py-0.5 text-[9px] text-slate-600"
                          title="Für diese Instanz ist die Reichweitenmessung nicht eingeschaltet."
                        >
                          misst nicht
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-1.5 text-slate-500">{s.kunde ?? '—'}</td>
                    <td className="px-3 py-1.5 text-right font-medium tabular-nums text-slate-800">
                      {s.besuche ? zahl(s.besuche) : '—'}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-slate-600">
                      {s.aufrufe ? zahl(s.aufrufe) : '—'}
                    </td>
                    <td className="px-3 py-1.5">
                      <Wandel prozent={s.wandelProzent} />
                    </td>
                    <td className="px-3 py-1.5">
                      <Verlauf werte={s.verlauf} />
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">
                      {s.einzelseitenAnteil !== null ? `${s.einzelseitenAnteil} %` : '—'}
                    </td>
                    <td className="px-3 py-1.5 text-right tabular-nums text-slate-500">
                      {s.dauerSchnittMs !== null ? (
                        <span className="inline-flex items-center gap-1">
                          <Clock size={9} className="text-slate-400" />
                          {Math.round(s.dauerSchnittMs / 1000)} s
                        </span>
                      ) : (
                        '—'
                      )}
                    </td>
                    <td className="px-3 py-1.5">
                      <span className="inline-flex items-center gap-1.5">
                        <Ampel befund={e} laedt={pruefen.isPending} />
                        {e && (
                          <span className="text-[10px] text-slate-500">
                            {e.status ?? '—'} · {e.dauerMs} ms
                          </span>
                        )}
                      </span>
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      {s.misst && (
                        <button
                          onClick={() => setOffen({ id: s.instanceId, name: s.name })}
                          title="Alle Zahlen dieser Seite"
                          className="inline-flex items-center gap-1 rounded border border-slate-300 px-1.5 py-0.5 text-[10px] font-medium text-slate-600 hover:bg-slate-50"
                        >
                          <BarChart3 size={10} /> Details
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
              {sortiert.length === 0 && (
                <tr>
                  <td colSpan={11} className="px-3 py-8 text-center text-slate-400">
                    {daten.isLoading
                      ? 'Wird geladen …'
                      : ohneMessungZeigen
                        ? 'Keine Seiten.'
                        : 'Keine Seite misst. Über das Kästchen oben rechts auch die übrigen zeigen.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <p className="border-t border-slate-100 px-4 py-2 text-[10px] leading-relaxed text-slate-500">
          <Bot size={10} className="mr-1 inline text-slate-400" />
          Erkannte Bots zählen nicht mit —{' '}
          {zahl(alle.reduce((s, x) => s + x.botsVerworfen, 0))} verworfen im Zeitraum, ebenso die
          Adressen unserer eigenen Server. Die Ampel ist dieselbe wie in der Adressübersicht einer
          Instanz: grün antwortet, gelb antwortet mit Einschränkung, rot antwortet nicht. Sie wird
          nur auf Knopfdruck gemessen.
        </p>
      </div>

      {offen && (
        <StatistikModal
          instanceId={offen.id}
          name={offen.name}
          onClose={() => setOffen(null)}
        />
      )}
    </div>
  );
}
